import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaTrash, FaCheck, FaTimes, FaEdit } from "react-icons/fa";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import ConfirmModal from "../../UI/ConfirmModal";

const DiagnosisManagement = () => {
    const { t } = useTranslation();
    const [diagnoses, setDiagnoses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");

    const [formData, setFormData] = useState({
        diagnosisName: "",
    });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const fetchDiagnoses = async () => {
        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const response = await fetch(`${apiChar}/diagnosis`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setDiagnoses(data || []);
            }
        } catch (error) {
            console.error("Error fetching diagnoses:", error);
            toast.error(t("Error fetching diagnoses"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDiagnoses();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!formData.diagnosisName.trim()) {
            toast.error(t("Diagnosis Name is required"));
            return;
        }

        setIsSaving(true);
        const token = getAuthToken();
        const apiChar = getApiChara();
        const url = `${apiChar}/diagnosis/addDiagnosis`;

        const payload = {
            id: 0,
            diagnosisName: formData.diagnosisName.trim(),
            option1: "",
            option2: ""
        };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success(t("Diagnosis added successfully!"));
                setFormData({ diagnosisName: "" });
                fetchDiagnoses();
            } else {
                throw new Error("Failed to add diagnosis");
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error adding diagnosis"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (diag) => {
        setEditingId(diag.id);
        setEditValue(diag.diagnosisName || "");
    };

    const handleInlineUpdate = async () => {
        if (!editValue.trim()) {
            toast.error(t("Diagnosis name cannot be empty"));
            return;
        }

        setIsSaving(true);
        const token = getAuthToken();
        const apiChar = getApiChara();
        const url = `${apiChar}/diagnosis/${editingId}`;

        const payload = {
            id: editingId,
            diagnosisName: editValue.trim(),
            option1: "",
            option2: ""
        };

        try {
            const response = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                toast.success(t("Diagnosis updated successfully!"));
                setEditingId(null);
                setEditValue("");
                fetchDiagnoses();
            } else {
                throw new Error("Failed to update diagnosis");
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error updating diagnosis"));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (id) => {
        setIdToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDeleteHandler = async () => {
        if (!idToDelete) return;

        const token = getAuthToken();
        const apiChar = getApiChara();
        setLoading(true);
        try {
            const response = await fetch(`${apiChar}/diagnosis/${idToDelete}`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                toast.success(t("Diagnosis deleted successfully!"));
                fetchDiagnoses();
            }
        } catch (error) {
            console.error(error);
            toast.error(t("Error deleting diagnosis"));
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
            setIdToDelete(null);
        }
    };

    return (
        <div className="diagnosis-management">
            <Form onSubmit={handleAddSubmit} className="mb-4 p-3 border rounded bg-light shadow-sm">
                <h5 className="mb-3" style={{ color: "var(--primary-color)", fontWeight: "700" }}>
                    {t("Add New Diagnosis")}
                </h5>
                <div className="row g-3 align-items-end">
                    <div className="col-md-9 col-12">
                        <Form.Label style={{ fontWeight: "600", fontSize: "0.85rem" }}>{t("Diagnosis Name")}</Form.Label>
                        <Form.Control
                            type="text"
                            name="diagnosisName"
                            value={formData.diagnosisName}
                            onChange={handleInputChange}
                            placeholder={t("Enter diagnosis name")}
                            style={{ fontSize: "0.9rem", border: "1px solid #ced4da" }}
                            required
                        />
                    </div>
                    <div className="col-md-3 col-12">
                        <Button type="submit" className="btn-premium btn-sm w-100 py-2 d-flex align-items-center justify-content-center" disabled={isSaving}>
                            <FaPlus className="me-2" /> {t("Add Diagnosis")}
                        </Button>
                    </div>
                </div>
            </Form>

            <div className="records-list mt-4">
                <h5 className="mb-3" style={{ color: "var(--primary-color)", fontWeight: "700" }}>{t("Saved Diagnoses")}</h5>

                <div className="table-responsive border rounded shadow-sm">
                    <Table striped hover className="mb-0 align-middle" style={{ fontSize: "0.85rem" }}>
                        <thead style={{ backgroundColor: "#f8f9fa" }}>
                            <tr className="bg-light">
                                <th style={{ width: "80px", textAlign: "center" }} className="py-3">{t("Sr.No")}</th>
                                <th className="py-3">{t("Diagnosis Name")}</th>
                                <th style={{ width: "160px" }} className="text-center py-3">{t("Actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && diagnoses.length === 0 ? (
                                <tr><td colSpan="3" className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
                            ) : diagnoses.length === 0 ? (
                                <tr><td colSpan="3" className="text-center py-4 text-muted">{t("No diagnoses found")}</td></tr>
                            ) : (
                                diagnoses.map((diag, index) => (
                                    <tr key={diag.id || index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td className="fw-500">
                                            {editingId === diag.id ? (
                                                <Form.Control
                                                    type="text"
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    size="sm"
                                                    autoFocus
                                                    className="border-primary"
                                                />
                                            ) : (
                                                diag.diagnosisName
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex justify-content-center gap-2">
                                                {editingId === diag.id ? (
                                                    <>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                            onClick={handleInlineUpdate}
                                                            title={t("Save")}
                                                            disabled={isSaving}
                                                            style={{ width: '32px', height: '32px' }}
                                                        >
                                                            {isSaving ? <Spinner animation="border" size="sm" /> : <FaCheck size={14} />}
                                                        </Button>
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                            onClick={() => setEditingId(null)}
                                                            title={t("Cancel")}
                                                            style={{ width: '32px', height: '32px' }}
                                                        >
                                                            <FaTimes size={14} />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="link"
                                                            className="text-primary p-1"
                                                            onClick={() => handleEdit(diag)}
                                                            title={t("Edit")}
                                                            disabled={editingId !== null}
                                                        >
                                                            <FaEdit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-1"
                                                            onClick={() => handleDelete(diag.id)}
                                                            title={t("Delete")}
                                                            disabled={editingId !== null}
                                                        >
                                                            <FaTrash size={16} />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Confirmation for Delete */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteHandler}
                title={t("Delete Diagnosis")}
                message={t("Are you sure you want to delete this diagnosis?")}
                variant="danger"
                isLoading={loading}
                confirmText={t("Delete")}
            />
        </div>
    );
};

export default DiagnosisManagement;
